import { camelCase, isArray, startCase } from 'lodash';
import {
    AgreementCode,
    EtfFilterInverseOptions,
    EtfFilterLeverageOptions,
    GlobalGroupCode,
    IndustrySector,
} from '../constants/lookups';
import { CryptoPair } from '../types';
import { divide } from './numberUtils';

export function titleCase(value: string): string {
    return startCase(camelCase(value));
}

export function parseOptionSymbol(optionSymbol: string) {
    try {
        // the length of an option symbol has 21 chars
        if (optionSymbol.length < 21) {
            return null;
        }

        const symbol = optionSymbol.substr(0, 6).trim();
        const dateString = optionSymbol.substr(6, 6);

        const date = new Date(
            Number('20' + dateString.substr(0, 2)),
            Number(dateString.substr(2, 2)) - 1,
            Number(dateString.substr(4, 2)),
        );
        const expirationDate = date;
        const call = optionSymbol[12] === 'C';
        const price = divide(Number(optionSymbol.substr(13)), 1000);

        return {
            symbol,
            expirationDate,
            call,
            price,
        };
    } catch {
        return null;
    }
}

export const getSectorByIndustryCode = (code: GlobalGroupCode) => {
    switch (code) {
        case GlobalGroupCode.BasicMaterial:
            return IndustrySector.BasicMaterial;
        case GlobalGroupCode.CommunicationServices:
            return IndustrySector.CommunicationServices;
        case GlobalGroupCode.ConsumerCyclical:
            return IndustrySector.ConsumerCyclical;
        case GlobalGroupCode.ConsumerDefensive:
            return IndustrySector.ConsumerDefensive;
        case GlobalGroupCode.Energy:
            return IndustrySector.Energy;
        case GlobalGroupCode.FinancialServices:
            return IndustrySector.FinancialServices;
        case GlobalGroupCode.Healthcare:
            return IndustrySector.Healthcare;
        case GlobalGroupCode.Industrials:
            return IndustrySector.Industrials;
        case GlobalGroupCode.RealEstate:
            return IndustrySector.RealEstate;
        case GlobalGroupCode.Technology:
            return IndustrySector.Technology;
        case GlobalGroupCode.Utilities:
            return IndustrySector.Utilities;

        default:
            return IndustrySector.Energy;
    }
};

export function parseCryptoSymbol(symbol: string | string[], separator = '/'): CryptoPair {
    let base = '';
    let quote = '';

    if (isArray(symbol)) {
        base = symbol[0] || '';
        quote = symbol[1] || '';
    } else {
        const symbols = symbol.toUpperCase().split(/[_\-/]/);
        base = symbols[0];
        quote = symbols[1];
    }

    return {
        base,
        quote,
        symbol: quote ? `${base}${separator}${quote}` : base,
        symbolNoSeparator: (base || '') + (quote || ''),
    };
}

export const getLeverageType = (id: EtfFilterLeverageOptions) => {
    switch (id) {
        case EtfFilterLeverageOptions.AllLeverage:
            return 'All Leverage ETFs';
        case EtfFilterLeverageOptions.Any:
            return 'Any';
        case EtfFilterLeverageOptions.Leveragex2:
            return 'Leverage x2';
        case EtfFilterLeverageOptions.Leveragex3:
            return 'Leverage x3';
        case EtfFilterLeverageOptions.NoLeverage:
            return 'No Leverage';
    }
};

export const getInverseType = (id: EtfFilterInverseOptions) => {
    switch (id) {
        case EtfFilterInverseOptions.Any:
            return 'Any';
        case EtfFilterInverseOptions.Inverse:
            return 'Inverse ETFs';
        case EtfFilterInverseOptions.LongEtf:
            return 'Long ETFs';
    }
};

export const getAgreementName = (code: AgreementCode) => {
    switch (code) {
        case AgreementCode.NyseAmex:
            return 'NYSE/AMEX';
        case AgreementCode.DowJones:
            return 'Dow Jones Index';
        case AgreementCode.Options:
            return 'Options';
        case AgreementCode.Nasdaq:
            return 'NASDAQ';
        case AgreementCode.SP500:
            return 'S&P 500';
        case AgreementCode.Canada:
            return 'Canada';
        case AgreementCode.CboeGlobal:
            return 'CBOE Global';
        default:
            return code;
    }
};
